// Add-to-cart -> side cart drawer (2026-08-25; generalised site-wide 2026-08-26)
// MADA's bespoke product forms ({% form 'product' %} on the PDP, PLP quick-add cards,
// recommendations, collection previews) natively POST to /cart/add and land on the full
// cart page. Horizon's stock product-form-component (AJAX add + CartAddEvent -> auto-open
// drawer) is NOT used by any of them. This module restores that behaviour for all of them
// without rewriting the forms: delegate on submit, add via AJAX with the cart-drawer
// section so it re-renders, then dispatch the theme's real CartAddEvent so the drawer
// refreshes (cart-items-component morphs) and opens.
//
// Delegation (not per-form binding) is deliberate: the PLP renders one form PER CARD and
// they all share id="product-form", so getElementById can only ever see the first one.
// Loaded once, site-wide, from layout/theme.liquid.

import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  // Only product forms (action /cart/add). The drawer/cart update form posts to /cart.
  if (!/\/cart\/add/.test(form.getAttribute('action') || '')) return;

  event.preventDefault();

  const variantId = form.querySelector('[name="id"]')?.value;
  if (!variantId) return; // nothing selected yet; no-op rather than a bad add

  const quantity = form.querySelector('input[name="quantity"]')?.value || '1';

  // Sections to re-render: every cart-items-component on the page (the drawer + cart page if present).
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);

  const body = new FormData();
  body.append('id', variantId);
  body.append('quantity', quantity);
  if (sectionIds.length) {
    body.append('sections', sectionIds.join(','));
    body.append('sections_url', window.location.pathname);
  }

  // Must be the .js endpoint: it returns JSON (with the requested `sections`), whereas /cart/add
  // returns an HTML page/redirect. Theme.routes isn't always defined on this theme, hence the fallback.
  const addUrl = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';
  const cfg = fetchConfig('javascript', { body });

  try {
    const response = await fetch(addUrl, {
      ...cfg,
      headers: { ...cfg.headers, Accept: 'application/json' },
    }).then((res) => res.json());

    // Shopify returns { status, message, ... } on failure.
    if (response.status) {
      // Fall back to the native cart page rather than swallowing the error silently.
      window.location.href = (window.Theme && Theme.routes && Theme.routes.cart_url) || '/cart';
      return;
    }

    // Dispatch the theme's own event so cart-items-component morphs from response.sections
    // (CartAddEvent.eventName === ThemeEvents.cartUpdate).
    document.dispatchEvent(
      new CartAddEvent({}, form.id || 'product-form', {
        source: 'product-form-component',
        itemCount: Number(quantity),
        variantId,
        sections: response.sections,
      })
    );

    // Open the drawer explicitly. Don't rely on the drawer's `auto-open` attribute: the request
    // is that add-to-cart ALWAYS opens the side cart, regardless of the global
    // auto_open_cart_drawer setting (which also differs between live and staging).
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    }
  } catch (error) {
    console.error('Add-to-cart failed, falling back to cart page:', error);
    window.location.href = (window.Theme && Theme.routes && Theme.routes.cart_url) || '/cart';
  }
});
